/**
 * SpotLightComponent - focused beam of light originating from a point.
 * Wraps THREE.SpotLight with distance, angle, and penumbra controls.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { LightComponent } from './LightComponent.js';

import type { LightComponentOptions } from './LightComponent.js';

export interface SpotLightComponentOptions extends LightComponentOptions {
  /** Maximum distance from the light where it has effect */
  distance?: number;
  /** How quickly the light's intensity diminishes with distance */
  decay?: number;
  /** Angle of the spotlight beam in radians */
  angle?: number;
  /** How quickly the light attenuates from the center of the beam */
  penumbra?: number;
}

/** Wraps a THREE.SpotLight. */
@EngineClass('SpotLightComponent')
export class SpotLightComponent extends LightComponent<THREE.SpotLight, SpotLightComponentOptions> {

  @property({ transient: true })
  get distance(): number {
    return this.light?.distance ?? 0;
  }
  set distance(value: number) {
    if (this.light) {
      this.light.distance = value;
    }
  }

  @property({ transient: true })
  get decay(): number {
    return this.light?.decay ?? 2;
  }
  set decay(value: number) {
    if (this.light) {
      this.light.decay = value;
    }
  }

  @property({ transient: true })
  get angle(): number {
    return this.light?.angle ?? Math.PI / 3;
  }
  set angle(value: number) {
    if (this.light) {
      this.light.angle = value;
    }
  }

  @property({ transient: true })
  get penumbra(): number {
    return this.light?.penumbra ?? 0;
  }
  set penumbra(value: number) {
    if (this.light) {
      this.light.penumbra = value;
    }
  }

  public override initialize(options?: SpotLightComponentOptions): void {
    super.initialize(options);
    this.distance = options?.distance ?? this.distance;
    this.decay = options?.decay ?? this.decay;
    this.angle = options?.angle ?? this.angle;
    this.penumbra = options?.penumbra ?? this.penumbra;
  }

  protected override createLight(): THREE.SpotLight {
    const light = new THREE.SpotLight();
    return light;
  }

  protected override createLightHelper(): THREE.Object3D | null {
    if (this.light) {
      return new THREE.SpotLightHelper(this.light);
    }
    return null;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_SpotLight';
  }
}
